import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Palette, RotateCcw, Download, Eye, RefreshCw } from 'lucide-react';

// ─── Default color palettes extracted from the Lottie files ───────────────
// These are the source colors we detect and replace in the Lottie JSON
interface ColorSlot {
  id: string;
  label: string;
  description: string;
  defaultRGBA: [number, number, number, number]; // 0-1 range as in Lottie
  category: 'skin' | 'clothing' | 'hair' | 'footwear' | 'shadow';
}

const FEMALE_COLOR_SLOTS: ColorSlot[] = [
  { id: 'skin_primary',       label: 'Skin (Primary)',     description: 'Main skin tone — hands, head, body, calves', defaultRGBA: [0.847, 0.608, 0.502, 1], category: 'skin' },
  { id: 'skin_secondary',     label: 'Skin (Secondary)',   description: 'Skin shadow areas — neck shadow, alternate hands', defaultRGBA: [0.804, 0.561, 0.459, 1], category: 'skin' },
  { id: 'hair',               label: 'Hair',               description: 'Hair color',                                  defaultRGBA: [0.0, 0.0, 0.0, 1], category: 'hair' },
  { id: 'clothing_top',       label: 'Top (Primary)',      description: 'Shirt / top garment — bright green',          defaultRGBA: [0.416, 0.859, 0.314, 1], category: 'clothing' },
  { id: 'clothing_top_light', label: 'Top (Sleeves)',      description: 'Sleeve highlights — lighter green',            defaultRGBA: [0.310, 0.718, 0.216, 1], category: 'clothing' },
  { id: 'clothing_top_shadow',label: 'Top (Shadow)',       description: 'Shirt shadow areas — dark green',              defaultRGBA: [0.184, 0.624, 0.082, 1], category: 'clothing' },
  { id: 'clothing_bottom',    label: 'Bottom (Primary)',   description: 'Leggings / pants — magenta/pink',              defaultRGBA: [0.820, 0.122, 0.400, 1], category: 'clothing' },
  { id: 'clothing_bottom_shadow', label: 'Bottom (Shadow)',description: 'Leggings shadow — darker pink',                defaultRGBA: [0.737, 0.157, 0.392, 1], category: 'clothing' },
  { id: 'footwear_primary',   label: 'Shoes (Primary)',    description: 'Shoe soles — lighter gray',                    defaultRGBA: [0.435, 0.435, 0.435, 1], category: 'footwear' },
  { id: 'footwear_secondary', label: 'Shoes (Secondary)',  description: 'Shoe soles alternate — darker gray',           defaultRGBA: [0.361, 0.361, 0.361, 1], category: 'footwear' },
];

// Male palette — same structure, potentially different default clothing colors
const MALE_COLOR_SLOTS: ColorSlot[] = [
  { id: 'skin_primary',       label: 'Skin (Primary)',     description: 'Main skin tone',                               defaultRGBA: [0.847, 0.608, 0.502, 1], category: 'skin' },
  { id: 'skin_secondary',     label: 'Skin (Secondary)',   description: 'Skin shadow areas',                            defaultRGBA: [0.804, 0.561, 0.459, 1], category: 'skin' },
  { id: 'hair',               label: 'Hair',               description: 'Hair color',                                   defaultRGBA: [0.0, 0.0, 0.0, 1], category: 'hair' },
  { id: 'clothing_top',       label: 'Top (Primary)',      description: 'Shirt / top garment',                          defaultRGBA: [0.416, 0.859, 0.314, 1], category: 'clothing' },
  { id: 'clothing_top_light', label: 'Top (Sleeves)',      description: 'Sleeve highlights',                            defaultRGBA: [0.310, 0.718, 0.216, 1], category: 'clothing' },
  { id: 'clothing_top_shadow',label: 'Top (Shadow)',       description: 'Shirt shadow areas',                           defaultRGBA: [0.184, 0.624, 0.082, 1], category: 'clothing' },
  { id: 'clothing_bottom',    label: 'Bottom (Primary)',   description: 'Shorts / pants',                               defaultRGBA: [0.820, 0.122, 0.400, 1], category: 'clothing' },
  { id: 'clothing_bottom_shadow', label: 'Bottom (Shadow)',description: 'Shorts shadow',                                defaultRGBA: [0.737, 0.157, 0.392, 1], category: 'clothing' },
  { id: 'footwear_primary',   label: 'Shoes (Primary)',    description: 'Shoe soles',                                   defaultRGBA: [0.435, 0.435, 0.435, 1], category: 'footwear' },
  { id: 'footwear_secondary', label: 'Shoes (Secondary)',  description: 'Shoe soles alternate',                         defaultRGBA: [0.361, 0.361, 0.361, 1], category: 'footwear' },
];

// Coach Cedar brand colors for quick-apply presets
const BRAND_PRESETS: Record<string, Record<string, string>> = {
  'Cedar Green': {
    clothing_top: '#2D6A4F',
    clothing_top_light: '#40916C',
    clothing_top_shadow: '#1B4332',
    clothing_bottom: '#1B1B1B',
    clothing_bottom_shadow: '#111111',
  },
  'Cedar Warm': {
    clothing_top: '#E76F51',
    clothing_top_light: '#F4A261',
    clothing_top_shadow: '#C44536',
    clothing_bottom: '#264653',
    clothing_bottom_shadow: '#1D3642',
  },
  'Midnight': {
    clothing_top: '#2B2D42',
    clothing_top_light: '#3D405B',
    clothing_top_shadow: '#1A1B2E',
    clothing_bottom: '#8D99AE',
    clothing_bottom_shadow: '#6B7B8D',
  },
  'Sunrise': {
    clothing_top: '#FF6B35',
    clothing_top_light: '#FF8C5A',
    clothing_top_shadow: '#CC5529',
    clothing_bottom: '#004E64',
    clothing_bottom_shadow: '#003B4D',
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────

function rgbaToHex(rgba: [number, number, number, number]): string {
  const r = Math.round(rgba[0] * 255);
  const g = Math.round(rgba[1] * 255);
  const b = Math.round(rgba[2] * 255);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function hexToRgba(hex: string): [number, number, number, number] {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;
  return [r, g, b, 1];
}

function colorsMatch(a: number[], b: number[], tolerance = 0.02): boolean {
  if (a.length < 3 || b.length < 3) return false;
  return Math.abs(a[0] - b[0]) < tolerance
      && Math.abs(a[1] - b[1]) < tolerance
      && Math.abs(a[2] - b[2]) < tolerance;
}

/** Walk a Lottie JSON tree and replace matching fill/stroke colors */
function recolorLottie(
  lottieData: any,
  colorMap: Array<{ from: [number, number, number, number]; to: [number, number, number, number] }>
): any {
  const data = JSON.parse(JSON.stringify(lottieData)); // deep clone

  function walk(obj: any) {
    if (typeof obj !== 'object' || obj === null) return;

    if (Array.isArray(obj)) {
      for (const item of obj) walk(item);
      return;
    }

    // Check if this is a fill (ty: "fl") or stroke (ty: "st") with a color
    if ((obj.ty === 'fl' || obj.ty === 'st') && obj.c) {
      const c = obj.c;
      if (c.a === 0 && Array.isArray(c.k) && c.k.length >= 3) {
        // Static color — check for match and replace
        for (const mapping of colorMap) {
          if (colorsMatch(c.k, mapping.from)) {
            c.k = [...mapping.to];
            break;
          }
        }
      }
    }

    for (const val of Object.values(obj)) {
      walk(val);
    }
  }

  walk(data);
  return data;
}

// ─── Lottie Web Player (minimal SVG renderer) ────────────────────────────

// We use lottie-web via CDN for the admin portal preview
declare global {
  interface Window {
    lottie: any;
  }
}

function useLottieScript(): boolean {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (window.lottie) { setLoaded(true); return; }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js';
    script.onload = () => setLoaded(true);
    document.head.appendChild(script);
    return () => { /* script persists intentionally */ };
  }, []);
  return loaded;
}

interface LottiePlayerProps {
  animationData: any;
  width?: number;
  height?: number;
  className?: string;
}

const LottiePlayer: React.FC<LottiePlayerProps> = ({ animationData, width = 320, height = 320, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<any>(null);
  const lottieReady = useLottieScript();

  useEffect(() => {
    if (!lottieReady || !containerRef.current || !animationData) return;
    // Destroy previous
    if (animRef.current) {
      animRef.current.destroy();
      animRef.current = null;
    }
    animRef.current = window.lottie.loadAnimation({
      container: containerRef.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: animationData,
    });
    return () => {
      if (animRef.current) {
        animRef.current.destroy();
        animRef.current = null;
      }
    };
  }, [lottieReady, animationData]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ width, height, background: 'transparent' }}
    />
  );
};

// ─── Color Picker Row ────────────────────────────────────────────────────

interface ColorRowProps {
  slot: ColorSlot;
  currentHex: string;
  onChange: (slotId: string, hex: string) => void;
}

const ColorRow: React.FC<ColorRowProps> = ({ slot, currentHex, onChange }) => {
  const categoryColors: Record<string, string> = {
    skin: 'bg-amber-50 border-amber-200',
    clothing: 'bg-blue-50 border-blue-200',
    hair: 'bg-gray-50 border-gray-200',
    footwear: 'bg-slate-50 border-slate-200',
    shadow: 'bg-gray-50 border-gray-200',
  };

  return (
    <div className={`flex items-center gap-3 p-2 rounded-lg border ${categoryColors[slot.category] || 'bg-white border-gray-200'}`}>
      <input
        type="color"
        value={currentHex}
        onChange={(e) => onChange(slot.id, e.target.value)}
        className="w-10 h-10 rounded-lg cursor-pointer border-2 border-white shadow-sm flex-shrink-0"
        style={{ padding: 0 }}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">{slot.label}</div>
        <div className="text-xs text-gray-500 truncate">{slot.description}</div>
      </div>
      <code className="text-xs text-gray-400 font-mono flex-shrink-0">{currentHex}</code>
    </div>
  );
};

// ─── Main Page Component ─────────────────────────────────────────────────

export const AdminLottieColorPreview: React.FC = () => {
  const [femaleColors, setFemaleColors] = useState<Record<string, string>>({});
  const [maleColors, setMaleColors] = useState<Record<string, string>>({});
  const [femaleLottieOriginal, setFemaleLottieOriginal] = useState<any>(null);
  const [maleLottieOriginal, setMaleLottieOriginal] = useState<any>(null);
  const [femaleLottieModified, setFemaleLottieModified] = useState<any>(null);
  const [maleLottieModified, setMaleLottieModified] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'female' | 'male'>('female');

  // Initialize default colors
  useEffect(() => {
    const fDefaults: Record<string, string> = {};
    for (const slot of FEMALE_COLOR_SLOTS) {
      fDefaults[slot.id] = rgbaToHex(slot.defaultRGBA);
    }
    setFemaleColors(fDefaults);

    const mDefaults: Record<string, string> = {};
    for (const slot of MALE_COLOR_SLOTS) {
      mDefaults[slot.id] = rgbaToHex(slot.defaultRGBA);
    }
    setMaleColors(mDefaults);
  }, []);

  // Load Lottie sample files
  useEffect(() => {
    const loadSamples = async () => {
      setLoading(true);
      try {
        // Female sample — the file you placed
        const femaleResp = await fetch('/assets/LottieExercises/Women/woman-doing-pike-push-up-exercise-for-shoulders.json');
        if (femaleResp.ok) {
          const data = await femaleResp.json();
          setFemaleLottieOriginal(data);
          setFemaleLottieModified(data);
        }
        // Male sample — will load when available
        const maleResp = await fetch('/assets/LottieExercises/Men/man-doing-pike-push-up-exercise-for-shoulders.json');
        if (maleResp.ok) {
          const data = await maleResp.json();
          setMaleLottieOriginal(data);
          setMaleLottieModified(data);
        }
      } catch (err) {
        console.warn('Lottie sample load error (some files may not exist yet):', err);
      }
      setLoading(false);
    };
    loadSamples();
  }, []);

  // Recolor Lottie when colors change
  const applyColors = useCallback(() => {
    if (femaleLottieOriginal) {
      const colorMap = FEMALE_COLOR_SLOTS.map(slot => ({
        from: slot.defaultRGBA,
        to: hexToRgba(femaleColors[slot.id] || rgbaToHex(slot.defaultRGBA)),
      }));
      setFemaleLottieModified(recolorLottie(femaleLottieOriginal, colorMap));
    }
    if (maleLottieOriginal) {
      const colorMap = MALE_COLOR_SLOTS.map(slot => ({
        from: slot.defaultRGBA,
        to: hexToRgba(maleColors[slot.id] || rgbaToHex(slot.defaultRGBA)),
      }));
      setMaleLottieModified(recolorLottie(maleLottieOriginal, colorMap));
    }
  }, [femaleColors, maleColors, femaleLottieOriginal, maleLottieOriginal]);

  useEffect(() => {
    applyColors();
  }, [applyColors]);

  const handleColorChange = (gender: 'female' | 'male', slotId: string, hex: string) => {
    if (gender === 'female') {
      setFemaleColors(prev => ({ ...prev, [slotId]: hex }));
    } else {
      setMaleColors(prev => ({ ...prev, [slotId]: hex }));
    }
    setActivePreset(null);
  };

  const applyPreset = (presetName: string) => {
    const preset = BRAND_PRESETS[presetName];
    if (!preset) return;
    setFemaleColors(prev => {
      const next = { ...prev };
      for (const [key, hex] of Object.entries(preset)) {
        next[key] = hex;
      }
      return next;
    });
    setMaleColors(prev => {
      const next = { ...prev };
      for (const [key, hex] of Object.entries(preset)) {
        next[key] = hex;
      }
      return next;
    });
    setActivePreset(presetName);
  };

  const resetToDefaults = () => {
    const fDefaults: Record<string, string> = {};
    for (const slot of FEMALE_COLOR_SLOTS) fDefaults[slot.id] = rgbaToHex(slot.defaultRGBA);
    setFemaleColors(fDefaults);

    const mDefaults: Record<string, string> = {};
    for (const slot of MALE_COLOR_SLOTS) mDefaults[slot.id] = rgbaToHex(slot.defaultRGBA);
    setMaleColors(mDefaults);
    setActivePreset(null);
  };

  const exportColorConfig = () => {
    const config = {
      version: '1.0.0',
      exported_at: new Date().toISOString(),
      female: Object.fromEntries(
        FEMALE_COLOR_SLOTS.map(slot => [slot.id, {
          hex: femaleColors[slot.id],
          rgba: hexToRgba(femaleColors[slot.id] || rgbaToHex(slot.defaultRGBA)),
          label: slot.label,
          category: slot.category,
        }])
      ),
      male: Object.fromEntries(
        MALE_COLOR_SLOTS.map(slot => [slot.id, {
          hex: maleColors[slot.id],
          rgba: hexToRgba(maleColors[slot.id] || rgbaToHex(slot.defaultRGBA)),
          label: slot.label,
          category: slot.category,
        }])
      ),
    };
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lottie-brand-colors.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const currentSlots = activeTab === 'female' ? FEMALE_COLOR_SLOTS : MALE_COLOR_SLOTS;
  const currentColors = activeTab === 'female' ? femaleColors : maleColors;
  const currentLottie = activeTab === 'female' ? femaleLottieModified : maleLottieModified;
  const originalLottie = activeTab === 'female' ? femaleLottieOriginal : maleLottieOriginal;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "'League Spartan', sans-serif", color: '#0B1614' }}>
            <Palette className="h-7 w-7" style={{ color: '#1F6C4F' }} />
            Lottie Exercise Colors
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Customize brand colors for exercise animations. Changes preview in real-time.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={resetToDefaults}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </button>
          <button
            onClick={exportColorConfig}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: '#1F6C4F' }}
          >
            <Download className="h-4 w-4" />
            Export Config
          </button>
        </div>
      </div>

      {/* Brand Presets */}
      <div className="bg-white p-4" style={{ borderRadius: '14px', boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
        <h3 className="text-sm font-medium text-gray-900 mb-3">Brand Presets</h3>
        <div className="flex gap-2 flex-wrap">
          {Object.entries(BRAND_PRESETS).map(([name, colors]) => (
            <button
              key={name}
              onClick={() => applyPreset(name)}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                activePreset === name
                  ? 'ring-2' + ' border-green-600 bg-green-50 text-green-800 ring-green-200'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex gap-0.5">
                <div className="w-4 h-4 rounded-sm border border-gray-200" style={{ backgroundColor: colors.clothing_top }} />
                <div className="w-4 h-4 rounded-sm border border-gray-200" style={{ backgroundColor: colors.clothing_bottom }} />
              </div>
              {name}
            </button>
          ))}
        </div>
      </div>

      {/* Main content: side-by-side preview + controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Animation Preview */}
        <div className="bg-white overflow-hidden" style={{ borderRadius: '14px', boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
          {/* Tab switcher */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('female')}
              className={`flex-1 py-3 px-4 text-sm font-medium text-center transition-colors ${
                activeTab === 'female'
                  ? 'border-b-2 border-green-700 text-green-800 bg-green-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              👩 Female
            </button>
            <button
              onClick={() => setActiveTab('male')}
              className={`flex-1 py-3 px-4 text-sm font-medium text-center transition-colors ${
                activeTab === 'male'
                  ? 'border-b-2 border-green-700 text-green-800 bg-green-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              👨 Male
            </button>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="h-8 w-8 text-gray-300 animate-spin" />
              </div>
            ) : currentLottie ? (
              <div className="flex flex-col items-center">
                {/* Side-by-side: Original + Modified */}
                <div className="flex gap-6 items-start">
                  <div className="text-center">
                    <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Original</div>
                    <div className="bg-gray-50 rounded-xl p-2 border border-gray-100">
                      <LottiePlayer animationData={originalLottie} width={200} height={200} />
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs font-medium uppercase tracking-wider mb-2 flex items-center justify-center gap-1" style={{ color: '#1F6C4F' }}>
                      <Eye className="h-3 w-3" />
                      Preview
                    </div>
                    <div className="bg-gray-50 rounded-xl p-2" style={{ border: '2px solid #1F6C4F' }}>
                      <LottiePlayer animationData={currentLottie} width={200} height={200} />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-4">
                  Animation: <span className="font-mono">{currentLottie.nm || 'Unknown'}</span>
                  {' · '}
                  {currentLottie.fr}fps · {((currentLottie.op - currentLottie.ip) / currentLottie.fr).toFixed(1)}s loop
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                <Palette className="h-12 w-12 mb-3 text-gray-300" />
                <p className="text-sm font-medium">No {activeTab} sample available</p>
                <p className="text-xs mt-1">
                  Place a Lottie JSON in<br/>
                  <code className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">
                    assets/LottieExercises/{activeTab === 'female' ? 'Women' : 'Men'}/
                  </code>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right: Color Controls */}
        <div className="bg-white overflow-hidden" style={{ borderRadius: '14px', boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
          <div className="p-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">
              Color Slots — {activeTab === 'female' ? 'Female' : 'Male'} Animations
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Click any swatch to change. Preview updates in real-time.</p>
          </div>

          <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
            {/* Group by category */}
            {(['skin', 'hair', 'clothing', 'footwear'] as const).map(category => {
              const slotsInCategory = currentSlots.filter(s => s.category === category);
              if (slotsInCategory.length === 0) return null;
              const categoryLabels: Record<string, string> = {
                skin: '🧑 Skin Tones',
                hair: '💇 Hair',
                clothing: '👕 Clothing',
                footwear: '👟 Footwear',
              };
              return (
                <div key={category}>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    {categoryLabels[category]}
                  </h4>
                  <div className="space-y-1.5">
                    {slotsInCategory.map(slot => (
                      <ColorRow
                        key={slot.id}
                        slot={slot}
                        currentHex={currentColors[slot.id] || rgbaToHex(slot.defaultRGBA)}
                        onChange={(id, hex) => handleColorChange(activeTab, id, hex)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Color Map Reference */}
      <div className="bg-gray-50 p-4" style={{ borderRadius: '14px', boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
        <h3 className="text-sm font-medium text-gray-700 mb-2">How Color Replacement Works</h3>
        <p className="text-xs text-gray-500 leading-relaxed">
          Lottie animations contain fill colors stored as RGBA arrays (0-1 range) in shape layers.
          This tool maps semantic color slots (skin, clothing, etc.) to the specific RGBA values used by the animation artist.
          At runtime on the device, the <code className="bg-white px-1 py-0.5 rounded font-mono">LottieExerciseView</code> component
          performs the same color walk-and-replace before passing data to the renderer.
          Export the config to save your brand colors as a JSON file that the app will use.
        </p>
      </div>
    </div>
  );
};

export default AdminLottieColorPreview;
